/* ===================================================================
   useDashboardSummary() -- KPI + chart data for the Dashboard screen.

   Binds to GET /api/v1/analytics/summary (engine/api/v1/app.py) through
   the typed api client (src/lib/api.ts). Disabled until a Bearer rr_
   token exists.

   The backend returns review_count_by_week / avg_rating_trend /
   response_rate_by_month as analytics blobs (typed `unknown` in api.ts
   until the analytics module's exact shape is frozen). The Dashboard
   screen maps those into its CHART / FUNNEL shapes at the screen
   boundary; this hook just delivers the payload + load/error state.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type AdminTenantDashboard, type AnalyticsSummary } from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { adminDashboardKey } from './useAdminTenantDashboard';
import { qk } from './queryKeys';

export interface UseDashboardSummaryParams {
  /** Force-disable even if a token exists. */
  enabled?: boolean;
}

export function useDashboardSummary(
  params: UseDashboardSummaryParams = {},
): UseQueryResult<AnalyticsSummary, Error> {
  const hasToken = Boolean(useApiToken());
  const admin = useAdminView();

  // OPTION A: in an operator read-only view, select the analytics slice out of
  // the single admin dashboard bundle (one cached fetch shared across panels).
  return useQuery({
    queryKey: admin ? adminDashboardKey(admin.clientId) : qk.dashboardSummary(),
    queryFn: ({ signal }) =>
      admin
        ? (api.getTenantDashboard(admin.clientId, signal) as Promise<unknown>)
        : (api.analyticsSummary(signal) as Promise<unknown>),
    select: admin
      ? (d) => (d as AdminTenantDashboard).analytics_summary
      : undefined,
    enabled: (admin ? true : hasToken) && params.enabled !== false,
    staleTime: 60_000,
  }) as UseQueryResult<AnalyticsSummary, Error>;
}
